import Bugsnag from "@bugsnag/js"
import { defineNuxtPlugin } from "#app"
import { isInjectedStackOverflow } from "~/utils/injectedStackOverflow"

// Functions placed in nuxt.config.ts `bugsnag.config` get serialized into
// runtimeConfig and silently dropped on their way to the browser. Register
// the onError callback from a real client plugin instead, so it survives
// into the production bundle.

const IPV4_HOST_URL = /^https?:\/\/\d{1,3}(\.\d{1,3}){3}(?::\d+)?\//
const IPV4_HOSTNAME = /^\d{1,3}(\.\d{1,3}){3}$/

export default defineNuxtPlugin(() => {
  if (!Bugsnag.isStarted()) return

  Bugsnag.addOnError((event) => {
    const error = event.errors[0]
    const message = error?.errorMessage ?? ""
    const frames = error?.stacktrace ?? []

    // Third-party ad/affiliate script noise (e.g. HTMLOUT from injected
    // tracker scripts) referencing globals we don't control.
    if (message.includes("HTMLOUT is not defined")) return false

    // Bing iOS in-app browser injects a script that throws
    // "Can't find variable: EmptyRanges" on first load. Frames are
    // unattributable so the message is the only signal.
    if (message.includes("EmptyRanges")) return false

    // Browser-extension content scripts. Firefox tags injected frames
    // with "> injectedScript" in the file URL; other extensions surface
    // moz-extension:// or chrome-extension:// frames. These commonly trip
    // cross-origin iframe access while walking the DOM. Gate on frame
    // origin — not just the message — so a real first-party cross-origin
    // bug would still report.
    const isExtensionError = frames.some((f) => {
      const file = f.file ?? ""
      return (
        file.includes("> injectedScript") ||
        file.includes("moz-extension://") ||
        file.includes("chrome-extension://")
      )
    })
    if (isExtensionError) return false

    // Web-proxy viewers: proxies rewrite _nuxt asset URLs to their own
    // IPv4 host (e.g. https://108.181.24.243/_nuxt/...), which breaks
    // module load order (producing Pinia "_s" undefined crashes) and
    // generates ungroupable minified noise from non-customers.
    if (frames.some((f) => IPV4_HOST_URL.test(f.file ?? ""))) return false
    if (IPV4_HOSTNAME.test(window.location.hostname)) return false

    // Stack overflows from scripts injected on the device (proxies, ad
    // blockers, page recorders) recursing through DOM/event handlers. Their
    // frames are either fileless or attributed to the document itself.
    if (isInjectedStackOverflow(message, frames, window.location.href)) {
      return false
    }
  })
})
